/**
 * D123a seam factory (Approach A — one shared factory, per-package injected
 * reporter). Returns `validate<T>`, the single monitor/enforce switch point
 * every boundary calls through.
 *
 * The #1 correctness discipline: `validate` ALWAYS returns the original
 * `value`, never `result.data`. Zod 4 `z.object()` strips unknown keys on
 * parse, so forwarding the parsed clone would silently drop fields a newer
 * deployed producer sends, breaking forward/backward compatibility (stale OBS
 * builds run for days; ROOM_STATE/FULL_STATE can replay Firestore-hydrated
 * older shapes). The return type `T` (the INPUT type, not the schema's
 * inferred output) makes the type checker enforce this — `result.data` is
 * typed `unknown`/the schema's output and cannot satisfy `T`.
 *
 * Dedupe is unified across every boundary that shares one `validate`
 * instance: once-per-(`schemaName`, `boundary`) via a closure-scoped Map,
 * mirroring the `useSupportedCards.ts` module-level cache precedent (persists
 * for the instance's lifetime — in practice the tab lifetime, since each
 * package instantiates `createBoundaryValidator` once at module scope).
 */
export function createBoundaryValidator(reporter) {
    const reportedKeys = new Set();
    return function validate(schema, value, ctx) {
        if (ctx.mode === 'enforce') {
            // D132 introduced the FIRST real `enforce` implementation, scoped to
            // `boundary === 'artifact'` (the source-keyed pricing artifact fetched
            // by web's `priceArtifact.ts`). D134 (CD decision) supersedes D123a's
            // original "do not extend this branch to any other boundary"
            // prohibition and widens this to a two-member allowlist — `'artifact'`
            // and `'fx'` (the FX rate doc read by web's FX client-read hook) — both
            // sourced from this same codebase's own pipelines, unlike the WS/HTTP
            // boundaries D123a's monitor-only scoping was written for. Every
            // OTHER boundary is still monitor-mode-only; adding a new enforce
            // boundary beyond this allowlist requires the same explicit CD
            // decision this one did, not a silent extension.
            if (!['artifact', 'fx'].includes(ctx.boundary)) {
                throw new Error('enforce not implemented — D123a monitor only');
            }
            const result = schema.safeParse(value);
            if (!result.success) {
                const issues = result.error.issues.map((issue) => ({
                    path: issue.path,
                    code: issue.code,
                }));
                reporter(issues, ctx);
                throw new Error(`${ctx.boundary} boundary validation failed: ${ctx.schemaName}`);
            }
            // Forward the ORIGINAL value — never `result.data`. See module doc.
            return value;
        }
        const result = schema.safeParse(value);
        if (!result.success) {
            const dedupeKey = `${ctx.boundary}:${ctx.schemaName}`;
            if (!reportedKeys.has(dedupeKey)) {
                reportedKeys.add(dedupeKey);
                const issues = result.error.issues.map((issue) => ({
                    path: issue.path,
                    code: issue.code,
                }));
                reporter(issues, ctx);
            }
        }
        // Forward the ORIGINAL value — never `result.data`. See module doc.
        return value;
    };
}
//# sourceMappingURL=boundaryValidator.js.map